// TESSERAX — INTENSITÉ MUSICALE ADAPTATIVE (cœur pur, testé).
//
// La musique GÉNÉRATIVE de main.ts (`scheduleStep`) escalade déjà par profondeur et
// combo (tempo, couches qui s'ouvrent, nappe de danger, balayage de Zone). Mais le jeu
// DÉMARRE sur un morceau enregistré (mp3) STATIQUE : l'expérience par défaut n'a aucune
// réactivité. Et le CONTINUUM (back-to-back de maîtrise — la colonne vertébrale du score,
// cf. core/scoring) n'a, lui, AUCUN retour musical, à aucune source.
//
// Ce module fournit un MODÈLE D'INTENSITÉ source-agnostique : il transforme l'état de
// jeu en un scalaire de TENSION 0..1, une LUEUR de continuum 0..1, et des consignes de
// gain/filtre pour une voix « bourdon » additive que main.ts pose PAR-DESSUS la musique
// (mp3 OU générative). Résultat : la tension du puits et la maîtrise s'ENTENDENT quelle
// que soit la source.
//
// PUR : aucune dépendance THREE / DOM / WebAudio / RNG. Le déterminisme du défi du jour
// est intact (l'audio n'altère ni le tirage ni le score).

const clamp01 = (x: number): number => x < 0 ? 0 : x > 1 ? 1 : x;

export interface MusicState {
  level: number;      // profondeur courante (1+)
  fill: number;       // remplissage du puits 0..1 (hauteur max / HEIGHT)
  holes: number;      // colonnes trouées (terrain actuel)
  combo: number;      // série d'enchaînement courante
  continuum: number;  // profondeur back-to-back courante (0/1 = aucun)
  zone: boolean;      // Zone active
}

// TENSION globale 0..1 — surtout le DANGER immédiat (remplissage du puits), rehaussé par
// les trous et un soupçon de profondeur. C'est le moteur du bourdon : plus le puits se
// remplit, plus la chambre « gronde ».
export function tension(s: MusicState): number {
  const fill = clamp01(s.fill || 0);
  const holes = clamp01((s.holes || 0) / 12);
  const depth = clamp01(((s.level || 1) - 1) / 12);
  return clamp01(fill * 0.6 + holes * 0.18 + depth * 0.22);
}

// LUEUR de continuum 0..1 — monte avec la profondeur du back-to-back. 0 tant qu'aucun
// continuum n'existe (count < 2). Pilote un carillon ascendant qui donne enfin une voix
// à l'axe de maîtrise.
export function continuumGlow(continuum: number): number {
  const c = continuum || 0;
  if (c < 2) return 0;
  return clamp01((c - 1) / 6);   // 2 → ~0.17 … 8 → 1
}

export interface MusicProfile {
  tension: number;   // 0..1 danger / instabilité
  glow: number;      // 0..1 continuum
  energy: number;    // 0..1 énergie globale (tension + combo + Zone + lueur) → brillance
  droneGain: number; // 0..1 gain conseillé pour le bourdon adaptatif
  filterHz: number;  // coupure passe-bas conseillée pour le bourdon (sombre → brillant)
}

// Profil complet : un seul appel par tick côté main.ts → gains/filtres du bourdon.
export function musicProfile(s: MusicState): MusicProfile {
  const t = tension(s);
  const glow = continuumGlow(s.continuum);
  const combo = clamp01((s.combo || 0) / 8);
  const energy = clamp01(t * 0.5 + combo * 0.3 + (s.zone ? 0.3 : 0) + glow * 0.2);
  // Le bourdon enfle avec la tension ; une présence plancher (0.08) ancre l'ambiance,
  // la Zone l'épaissit un peu (la chambre « respire » le pouvoir).
  const droneGain = clamp01(0.08 + t * 0.5 + (s.zone ? 0.12 : 0));
  const filterHz = 300 + energy * 2600;   // 300 Hz (sombre, puits calme) → ~2900 Hz (brillant)
  return { tension: t, glow, energy, droneGain, filterHz };
}

// Fréquences d'un CARILLON de continuum (Hz) : un arpège pentatonique ascendant dont la
// hauteur et la longueur croissent avec la profondeur du back-to-band. `root` = racine
// harmonique courante (main.ts passe ROOTS du monde). Pur : ne joue rien, calcule des Hz.
export function continuumChimeFreqs(continuum: number, root: number): number[] {
  const c = continuum || 0;
  if (c < 2) return [];
  const PENT = [0, 3, 5, 7, 10];                 // pentatonique mineure (cf. core/harmony)
  const steps = Math.min(5, c - 1);              // 2→1 note … 6+→5 notes
  const octave = c >= 5 ? 8 : 4;                 // monte d'une octave quand le continuum est profond
  const out: number[] = [];
  for (let i = 0; i < steps; i++) out.push(root * octave * Math.pow(2, PENT[i % PENT.length] / 12));
  return out;
}
